/**
 * Epic 12 Phase B: Triage Wizard — symptom-first → likely FM → runbook link.
 * Steps: 1) Select symptom (Top-20) 2) Likely FM + cause + link 3) Auto checks 4) Next actions.
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Link,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { RUNBOOK_TOP20, runbookDocUrl, type RunbookTop20Item } from '../data/runbookTop20';
import { deriveOpsState } from '../services/opsState';

export interface TriageWizardChecks {
  uiCloud?: { status: string; detail?: unknown };
  edgeHeartbeat?: { ok?: boolean; age_s?: number } | null;
  configDrift?: { overall_status?: string } | null;
  postDeployHealth?: { status?: string; degraded_total?: number } | null;
}

export interface TriageWizardProps {
  open: boolean;
  onClose: () => void;
  /** Read-only check results from the parent page (Diagnostics). */
  checks?: TriageWizardChecks;
}

const steps = ['Select symptom', 'Likely failure mode', 'Auto checks', 'Next actions'];

export const TriageWizard: React.FC<TriageWizardProps> = ({ open, onClose, checks = {} }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<RunbookTop20Item | null>(null);

  const opsState = useMemo(() => deriveOpsState({}), []);

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return RUNBOOK_TOP20;
    return RUNBOOK_TOP20.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.symptom.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [search]);

  const handleClose = () => {
    setActiveStep(0);
    setSelected(null);
    setSearch('');
    onClose();
  };

  const handleSelect = (item: RunbookTop20Item) => {
    setSelected(item);
    setActiveStep(1);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Troubleshoot — symptom-first triage</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Search symptom or failure mode…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 2 }}
            />
            <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
              {filtered.map((item) => (
                <ListItemButton key={item.id} onClick={() => handleSelect(item)} selected={selected?.id === item.id}>
                  <ListItemText primary={`${item.id}: ${item.title}`} secondary={item.symptom} />
                </ListItemButton>
              ))}
              {filtered.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No matches. Try a different search.
                </Typography>
              )}
            </List>
          </Box>
        )}

        {activeStep === 1 && selected && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Likely failure mode:</strong> {selected.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Symptom: {selected.symptom}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Likely cause:</strong> {selected.cause}
            </Typography>
            <Link href={runbookDocUrl(selected.anchor)} target="_blank" rel="noopener noreferrer">
              Open runbook: {selected.id} →
            </Link>
            <Box sx={{ mt: 2 }}>
              <Button size="small" onClick={() => setActiveStep(2)}>
                View auto checks
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Auto checks (read-only)
            </Typography>
            <List dense>
              <ListItemButton disabled>
                <ListItemText
                  primary="UI / Cloud reachability"
                  secondary={`Status: ${opsState.state}`}
                />
              </ListItemButton>
              <ListItemButton disabled>
                <ListItemText
                  primary="Edge heartbeat"
                  secondary={
                    checks.edgeHeartbeat != null
                      ? checks.edgeHeartbeat.ok
                        ? `OK (${checks.edgeHeartbeat.age_s ?? '—'}s ago)`
                        : 'Offline or unknown'
                      : 'Not available here (select a device)'
                  }
                />
              </ListItemButton>
              <ListItemButton disabled>
                <ListItemText
                  primary="Config drift"
                  secondary={
                    checks.configDrift != null
                      ? `Overall: ${checks.configDrift.overall_status ?? '—'}`
                      : 'Not available here'
                  }
                />
              </ListItemButton>
              <ListItemButton disabled>
                <ListItemText
                  primary="Post-deploy health"
                  secondary={
                    checks.postDeployHealth != null
                      ? `Status: ${checks.postDeployHealth.status ?? '—'}${checks.postDeployHealth.degraded_total != null ? `; degraded: ${checks.postDeployHealth.degraded_total}` : ''}`
                      : 'Not available here'
                  }
                />
              </ListItemButton>
            </List>
            <Box sx={{ mt: 2 }}>
              <Button size="small" onClick={() => setActiveStep(3)}>
                Next actions
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 3 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Follow the runbook for your selected failure mode. Use the link above to open the full runbook.
            </Alert>
            <Typography variant="body2">
              Next steps: 1) Verify the likely cause matches your symptoms. 2) Run the Fix commands from the runbook. 3) If
              the issue persists, copy the context bundle and escalate to support.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep((s) => Math.max(0, s - 1))}>Back</Button>
        )}
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
